import './SOAPNoteDisplay.css';
import { useState, useRef } from 'react';
import { FormattedText } from '../common/FormattedText';
import { exportSOAPNoteToPDF } from '../../utils/soapExport';

export const SOAPNoteDisplay = ({
  soapNote,
  editedSoapNote,
  isEditing: isSoapEditing,
  copied,
  isSaved,
  hasUnsavedChanges,
  isRegeneratingSupport,
  isSavingHistory,
  patientName,
  appointmentDate,
  fileName,
  audioFile,
  editingCodeIndex,
  editedCodeValue,
  setEditedCodeValue,
  editedCodeDescription,
  setEditedCodeDescription,
  editedCodeTiming,
  setEditedCodeTiming,
  handleEditSOAP,
  handleSaveEdit,
  handleCancelEdit,
  handleEditField,
  handleCloseSoapNote,
  handleCopySOAP,
  startEditingCode,
  saveEditedCode,
  cancelEditingCode,
  addNewCode,
  deleteCode,
  saveToHistory,
  isDemoMode = false,
  currentUser,
}) => {
  const [showTranscript, setShowTranscript] = useState(false);
  const [showRecording, setShowRecording] = useState(false);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingCurrentTime, setRecordingCurrentTime] = useState(0);
  const recordingAudioRef = useRef(null);

  // Format time for audio player display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (!soapNote) return null;

  return (
    <>
      {/* Mobile Close Button - shown above container on small screens */}
      <div className="mobile-close-button">
        <button onClick={handleCloseSoapNote}>
          ×
        </button>
      </div>

      <div className="soap-note-container">
        <div className="soap-note-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '0' }}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, width: '20px', height: '20px' }}>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.95rem' }}>
              <span style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>SOAP Note Generated</span>
              {patientName && (
                <span style={{ color: '#555', whiteSpace: 'nowrap' }}>for <strong>{patientName}</strong></span>
              )}
              {appointmentDate && (
                <span style={{ color: '#555', whiteSpace: 'nowrap' }}>on <strong>{(() => {
                  const [year, month, day] = appointmentDate.split('-');
                  return new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                })()}</strong></span>
              )}
              {fileName && (
                <span style={{ color: '#555', whiteSpace: 'nowrap' }}>📎 <strong>{fileName}</strong></span>
              )}
              {currentUser?.full_name && (
                <span style={{ color: '#888', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Dr. {currentUser.full_name}</span>
              )}
              {currentUser?.license_number && (
                <span style={{ color: '#888', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>#{currentUser.license_number}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {soapNote.soap_generated && soapNote.soap_note && !isSoapEditing && (
              <>
                <button
                  onClick={handleEditSOAP}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.35rem',
                    padding: '0.5rem 0.9rem',
                    background: '#ed8936',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#dd6b20'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#ed8936'}
                  title="Edit SOAP note"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Edit
                </button>
                <button
                  onClick={handleCopySOAP}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.35rem',
                    padding: '0.5rem 0.9rem',
                    background: copied ? '#48bb78' : '#4299e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={(e) => {
                    if (!copied) e.currentTarget.style.background = '#3182ce';
                  }}
                  onMouseOut={(e) => {
                    if (!copied) e.currentTarget.style.background = '#4299e1';
                  }}
                  title={copied ? 'Copied to clipboard' : 'Copy SOAP note'}
                >
                  {copied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="9" y="9" width="13" height="13" rx="2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={saveToHistory}
                  disabled={isSavingHistory || (isSaved && !hasUnsavedChanges)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.35rem',
                    padding: '0.5rem 0.9rem',
                    background: (isSaved && !hasUnsavedChanges) ? '#10b981' : '#9f7aea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: (isSavingHistory || (isSaved && !hasUnsavedChanges)) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    opacity: (isSavingHistory || (isSaved && !hasUnsavedChanges)) ? 0.6 : 1,
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={(e) => {
                    if (!isSavingHistory && !(isSaved && !hasUnsavedChanges)) {
                      e.currentTarget.style.background = '#805ad5';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSavingHistory && !(isSaved && !hasUnsavedChanges)) {
                      e.currentTarget.style.background = '#9f7aea';
                    }
                  }}
                  title={isSaved && !hasUnsavedChanges ? 'Saved' : 'Save to History'}
                >
                  {isSavingHistory ? (
                    <>
                      <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" opacity="0.25"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                      </svg>
                      Saving...
                    </>
                  ) : (isSaved && !hasUnsavedChanges) ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Saved
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={() => exportSOAPNoteToPDF({
                    patientName,
                    appointmentDate,
                    fileName,
                    soapNote,
                    dentistName: currentUser?.full_name,
                    licenseNumber: currentUser?.license_number,
                  })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.35rem',
                    padding: '0.5rem 0.9rem',
                    background: '#48bb78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#38a169'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#48bb78'}
                  title="Export SOAP note as PDF"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 13v6m-3-3h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  PDF
                </button>
              </>
            )}
            <button
              onClick={handleCloseSoapNote}
              style={{
                flexShrink: 0,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#ef4444',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                padding: 0,
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
              title="Close SOAP note"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="soap-note-content">
          {/* Show PII Redaction Info */}
          {soapNote.pii_redacted && soapNote.pii_found && soapNote.pii_found.length > 0 && (
            <div className="pii-notification" style={{ marginBottom: '2rem' }}>
              <div className="pii-notification-content">
                <svg className="pii-notification-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="pii-notification-text">
                  <strong>Personal Identifiers Removed</strong>
                  <div className="pii-tags">
                    {soapNote.pii_found.map((pii, index) => (
                      <span key={index} className="pii-tag">{pii.type.toUpperCase()}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Transcript and Recording Buttons Container */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            {/* Transcript Button */}
            {soapNote.transcript && (
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: showTranscript ? '#4299e1' : '#e2e8f0',
                  color: showTranscript ? 'white' : '#2d3748',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = showTranscript ? '#3182ce' : '#cbd5e0';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = showTranscript ? '#4299e1' : '#e2e8f0';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
            )}

            {/* Recording Button */}
            {audioFile && (
              <button
                onClick={() => setShowRecording(!showRecording)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: showRecording ? '#4299e1' : '#e2e8f0',
                  color: showRecording ? 'white' : '#2d3748',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = showRecording ? '#3182ce' : '#cbd5e0';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = showRecording ? '#4299e1' : '#e2e8f0';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {showRecording ? 'Hide Recording' : 'Show Recording'}
              </button>
            )}
          </div>

          {/* Transcript Display */}
          {showTranscript && soapNote.transcript && (
            <div style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#2d3748', fontWeight: '600' }}>
                Original Transcript
              </h4>
              <div style={{
                lineHeight: '1.8',
                fontSize: '1rem',
                color: '#4a5568',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
                {soapNote.transcript}
              </div>
            </div>
          )}

          {/* Recording Display */}
          {showRecording && audioFile && (
            <div style={{
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              backgroundColor: 'rgba(17, 94, 89, 0.2)',
              border: '2px solid rgba(17, 94, 89, 0.4)',
              borderRadius: '2rem',
              overflow: 'hidden'
            }}>
              <audio
                ref={recordingAudioRef}
                onTimeUpdate={() => {
                  if (recordingAudioRef.current) {
                    setRecordingCurrentTime(recordingAudioRef.current.currentTime);
                  }
                }}
                onEnded={() => setIsPlayingRecording(false)}
                onLoadedMetadata={() => {
                  if (recordingAudioRef.current) {
                    setRecordingDuration(recordingAudioRef.current.duration);
                  }
                }}
              >
                <source src={audioFile ? URL.createObjectURL(audioFile) : ''} type={audioFile?.type || 'audio/mpeg'} />
              </audio>

              {/* Play button */}
              <button
                onClick={() => {
                  if (!recordingAudioRef.current) return;
                  if (isPlayingRecording) {
                    recordingAudioRef.current.pause();
                    setIsPlayingRecording(false);
                  } else {
                    recordingAudioRef.current.play().catch(err => console.error('Error playing audio:', err));
                    setIsPlayingRecording(true);
                  }
                }}
                style={{
                  flexShrink: 0,
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  padding: 0,
                  transition: 'background-color 0.2s'
                }}
                aria-label={isPlayingRecording ? 'Pause audio' : 'Play audio'}
              >
                {isPlayingRecording ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>

              {/* File name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, color: '#000', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {fileName || 'Audio Recording'}
                </p>
              </div>

              {/* Progress bar and duration */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  onClick={(e) => {
                    if (!recordingAudioRef.current || !recordingDuration) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percent = (e.clientX - rect.left) / rect.width;
                    const newTime = percent * recordingDuration;
                    recordingAudioRef.current.currentTime = newTime;
                    setRecordingCurrentTime(newTime);
                  }}
                  style={{
                    flex: 1,
                    height: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    height: '100%',
                    backgroundColor: '#10b981',
                    width: `${recordingDuration > 0 ? (recordingCurrentTime / recordingDuration) * 100 : 0}%`,
                    borderRadius: '2px',
                    transition: 'width 0.1s linear'
                  }}></div>
                </div>
                <span style={{ color: '#000', fontSize: '0.75rem', whiteSpace: 'nowrap', fontWeight: 500 }}>
                  {formatTime(recordingCurrentTime)} / {formatTime(recordingDuration)}
                </span>
              </div>
            </div>
          )}

          {/* Display SOAP Note if available */}
          {soapNote.soap_generated && soapNote.soap_note ? (
            <>
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ marginBottom: '2rem', borderLeft: '4px solid #4299e1', paddingLeft: '1.5rem' }}>
                  <h3 style={{
                    color: '#2d3748',
                    marginBottom: '0.75rem',
                    fontWeight: '700',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px'
                  }}>
                    Subjective
                  </h3>
                  {isSoapEditing ? (
                    <textarea
                      value={editedSoapNote.subjective || ''}
                      onChange={(e) => handleEditField('subjective', e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '150px',
                        padding: '1rem',
                        fontSize: '1.1rem',
                        lineHeight: '1.8',
                        color: '#4a5568',
                        border: '2px solid #4299e1',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        cursor: 'pointer'
                      }}
                    />
                  ) : (
                    <div style={{
                      lineHeight: '1.8',
                      fontSize: '1.1rem',
                      color: '#4a5568'
                    }}>
                      <FormattedText text={(editedSoapNote || soapNote.soap_note).subjective || 'N/A'} />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '2rem', borderLeft: '4px solid #48bb78', paddingLeft: '1.5rem' }}>
                  <h3 style={{
                    color: '#2d3748',
                    marginBottom: '0.75rem',
                    fontWeight: '700',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px'
                  }}>
                    Objective
                  </h3>
                  {isSoapEditing ? (
                    <textarea
                      value={editedSoapNote.objective || ''}
                      onChange={(e) => handleEditField('objective', e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '150px',
                        padding: '1rem',
                        fontSize: '1.1rem',
                        lineHeight: '1.8',
                        color: '#4a5568',
                        border: '2px solid #48bb78',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        cursor: 'pointer'
                      }}
                    />
                  ) : (
                    <div style={{
                      lineHeight: '1.8',
                      fontSize: '1.1rem',
                      color: '#4a5568'
                    }}>
                      <FormattedText text={(editedSoapNote || soapNote.soap_note).objective || 'N/A'} />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '2rem', borderLeft: '4px solid #ed8936', paddingLeft: '1.5rem' }}>
                  <h3 style={{
                    color: '#2d3748',
                    marginBottom: '0.75rem',
                    fontWeight: '700',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px'
                  }}>
                    Assessment
                  </h3>
                  {isSoapEditing ? (
                    <textarea
                      value={editedSoapNote.assessment || ''}
                      onChange={(e) => handleEditField('assessment', e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '150px',
                        padding: '1rem',
                        fontSize: '1.1rem',
                        lineHeight: '1.8',
                        color: '#4a5568',
                        border: '2px solid #ed8936',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        cursor: 'pointer'
                      }}
                    />
                  ) : (
                    <div style={{
                      lineHeight: '1.8',
                      fontSize: '1.1rem',
                      color: '#4a5568'
                    }}>
                      <FormattedText text={(editedSoapNote || soapNote.soap_note).assessment || 'N/A'} />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '2rem', borderLeft: '4px solid #9f7aea', paddingLeft: '1.5rem' }}>
                  <h3 style={{
                    color: '#2d3748',
                    marginBottom: '0.75rem',
                    fontWeight: '700',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px'
                  }}>
                    Plan
                  </h3>
                  {isSoapEditing ? (
                    <textarea
                      value={editedSoapNote.plan || ''}
                      onChange={(e) => handleEditField('plan', e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '150px',
                        padding: '1rem',
                        fontSize: '1.1rem',
                        lineHeight: '1.8',
                        color: '#4a5568',
                        border: '2px solid #9f7aea',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        cursor: 'pointer'
                      }}
                    />
                  ) : (
                    <div style={{
                      lineHeight: '1.8',
                      fontSize: '1.1rem',
                      color: '#4a5568'
                    }}>
                      <FormattedText text={(editedSoapNote || soapNote.soap_note).plan || 'N/A'} />
                    </div>
                  )}
                </div>

                {/* AI Clinical Support Section */}
                {soapNote.ai_support_generated && soapNote.clinical_support && (
                  <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '2px solid #e2e8f0' }}>
                    <h2 style={{
                      color: '#2d3748',
                      marginBottom: '1.5rem',
                      fontSize: '1.8rem',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      AI Clinical Support
                    </h2>

                    {/* Clinical Recommendations */}
                    {soapNote.clinical_support.clinical_recommendations && soapNote.clinical_support.clinical_recommendations.length > 0 && (
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#2c5282', fontSize: '1.2rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                          Clinical Recommendations
                        </h4>
                        <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8', fontSize: '1.05rem', color: '#4a5568' }}>
                          {soapNote.clinical_support.clinical_recommendations.map((rec, idx) => (
                            <li key={idx} style={{ marginBottom: '0.5rem' }}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Patient Education */}
                    {soapNote.clinical_support.patient_education && soapNote.clinical_support.patient_education.length > 0 && (
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#2c5282', fontSize: '1.2rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                          Patient Education
                        </h4>
                        <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8', fontSize: '1.05rem', color: '#4a5568' }}>
                          {soapNote.clinical_support.patient_education.map((edu, idx) => (
                            <li key={idx} style={{ marginBottom: '0.5rem' }}>{edu}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Billing Codes */}
                    {soapNote.clinical_support.billing_codes && soapNote.clinical_support.billing_codes.length > 0 && (
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#2c5282', fontSize: '1.2rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                          CDA Billing Codes
                        </h4>
                        <p style={{
                          fontSize: '0.95rem',
                          color: isDemoMode ? '#047857' : '#718096',
                          marginBottom: '1rem',
                          fontStyle: 'italic',
                          padding: '0.5rem',
                          backgroundColor: isDemoMode ? '#ecfdf5' : '#f7fafc',
                          borderLeft: `3px solid ${isDemoMode ? '#10b981' : '#cbd5e0'}`,
                          lineHeight: '1.5'
                        }}>
                          {isDemoMode
                            ? '💡 Demo Mode: Editing codes, deleting codes, and adding codes are unavailable. Sign up to access full features and manage billing codes.'
                            : 'Note: These billing codes are AI-generated suggestions and may not be 100% accurate. Please verify all codes with your dental provider or billing specialist before submission.'
                          }
                        </p>

                        {/* Check if codes have step/timing info (table format) */}
                        {soapNote.clinical_support.billing_codes[0].step !== undefined ? (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: '1.05rem',
                              color: '#4a5568'
                            }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #2c5282' }}>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#2c5282', fontWeight: '600' }}>Procedure</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#2c5282', fontWeight: '600' }}>CDA Code</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#2c5282', fontWeight: '600' }}>Confidence</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#2c5282', fontWeight: '600' }}>Timing</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#2c5282', fontWeight: '600' }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...soapNote.clinical_support.billing_codes]
                                  .sort((a, b) => {
                                    const timingA = a.timing?.toLowerCase() || '';
                                    const timingB = b.timing?.toLowerCase() || '';
                                    if (timingA.includes('today')) return -1;
                                    if (timingB.includes('today')) return 1;
                                    if (timingA.includes('next')) return -1;
                                    if (timingB.includes('next')) return 1;
                                    if (timingA.includes('after') && !timingB.includes('after')) return -1;
                                    if (timingB.includes('after') && !timingA.includes('after')) return 1;
                                    return 0;
                                  })
                                  .map((code, idx) => {
                                    const originalIndex = soapNote.clinical_support.billing_codes.indexOf(code);
                                    const isCodeEditing = editingCodeIndex === originalIndex;

                                    return (
                                      <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '0.75rem' }}>
                                          {isCodeEditing ? (
                                            <input
                                              type="text"
                                              value={editedCodeDescription}
                                              onChange={(e) => setEditedCodeDescription(e.target.value)}
                                              style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                border: '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem'
                                              }}
                                              placeholder="Procedure description"
                                            />
                                          ) : (
                                            code.description
                                          )}
                                        </td>
                                        <td style={{ padding: '0.75rem' }}>
                                          {isCodeEditing ? (
                                            <input
                                              type="text"
                                              value={editedCodeValue}
                                              onChange={(e) => setEditedCodeValue(e.target.value)}
                                              style={{
                                                width: '100px',
                                                padding: '0.5rem',
                                                border: '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                fontWeight: '600'
                                              }}
                                              placeholder="Code"
                                            />
                                          ) : (
                                            <strong>{code.code}</strong>
                                          )}
                                        </td>
                                        <td style={{ padding: '0.75rem' }}>
                                          {!isCodeEditing && code.confidence && (
                                            <span style={{
                                              fontSize: '0.8rem',
                                              padding: '0.2rem 0.5rem',
                                              borderRadius: '12px',
                                              fontWeight: '600',
                                              backgroundColor: parseFloat(code.confidence) >= 0.9 ? '#d4edda' :
                                                              parseFloat(code.confidence) >= 0.8 ? '#fff3cd' :
                                                              parseFloat(code.confidence) >= 0.7 ? '#ffe5d0' : '#f8d7da',
                                              color: parseFloat(code.confidence) >= 0.9 ? '#155724' :
                                                     parseFloat(code.confidence) >= 0.8 ? '#856404' :
                                                     parseFloat(code.confidence) >= 0.7 ? '#cc5200' : '#721c24'
                                            }}>
                                              {(parseFloat(code.confidence) * 100).toFixed(0)}%
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ padding: '0.75rem' }}>
                                          {isCodeEditing ? (
                                            <select
                                              value={editedCodeTiming}
                                              onChange={(e) => setEditedCodeTiming(e.target.value)}
                                              style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                border: '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                fontSize: '1rem',
                                                backgroundColor: 'white',
                                                cursor: 'pointer'
                                              }}
                                            >
                                              <option value="Today">Today</option>
                                              <option value="In 1 week">In 1 week</option>
                                              <option value="In 2 weeks">In 2 weeks</option>
                                              <option value="In 2-4 weeks">In 2-4 weeks</option>
                                              <option value="In 1 month">In 1 month</option>
                                              <option value="In 3 months">In 3 months</option>
                                              <option value="In 6 months">In 6 months</option>
                                              <option value="Next visit">Next visit</option>
                                            </select>
                                          ) : (
                                            code.timing
                                          )}
                                        </td>
                                        <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                                          {isCodeEditing ? (
                                            <>
                                              <button
                                                onClick={() => saveEditedCode(originalIndex)}
                                                style={{
                                                  padding: '0.4rem 0.8rem',
                                                  background: '#48bb78',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: '0.9rem',
                                                  marginRight: '0.5rem'
                                                }}
                                                title="Save changes"
                                              >
                                                ✓
                                              </button>
                                              <button
                                                onClick={cancelEditingCode}
                                                style={{
                                                  padding: '0.4rem 0.8rem',
                                                  background: '#718096',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: '0.9rem'
                                                }}
                                                title="Cancel"
                                              >
                                                ✕
                                              </button>
                                            </>
                                          ) : (
                                            <>
                                              <button
                                                onClick={() => startEditingCode(originalIndex, code)}
                                                style={{
                                                  padding: '0.35rem 0.7rem',
                                                  background: '#edf2f7',
                                                  color: '#2c5282',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: '0.8rem',
                                                  fontWeight: '500',
                                                  marginRight: '0.4rem',
                                                  letterSpacing: '0.01em'
                                                }}
                                                title="Edit code"
                                              >
                                                Edit
                                              </button>
                                              <button
                                                onClick={() => deleteCode(originalIndex)}
                                                style={{
                                                  padding: '0.35rem 0.7rem',
                                                  background: '#fff5f5',
                                                  color: '#c53030',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  cursor: 'pointer',
                                                  fontSize: '0.8rem',
                                                  fontWeight: '500',
                                                  letterSpacing: '0.01em'
                                                }}
                                                title="Delete code"
                                              >
                                                Remove
                                              </button>
                                            </>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                            <button
                              onClick={addNewCode}
                              style={{
                                marginTop: '1rem',
                                padding: '0.6rem 1.2rem',
                                background: '#48bb78',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              + Add Code
                            </button>

                          </div>
                        ) : (
                          /* Legacy bullet list format */
                          <>
                            <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8', fontSize: '1.05rem', color: '#4a5568' }}>
                              {soapNote.clinical_support.billing_codes.map((code, idx) => {
                                const isCodeEditing = editingCodeIndex === idx;

                                return (
                                  <li key={idx} style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {isCodeEditing ? (
                                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                                        <input
                                          type="text"
                                          value={editedCodeValue}
                                          onChange={(e) => setEditedCodeValue(e.target.value)}
                                          style={{
                                            width: '100px',
                                            padding: '0.4rem',
                                            border: '1px solid #cbd5e0',
                                            borderRadius: '6px',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                          }}
                                          placeholder="Code"
                                        />
                                        <span>-</span>
                                        <input
                                          type="text"
                                          value={editedCodeDescription}
                                          onChange={(e) => setEditedCodeDescription(e.target.value)}
                                          style={{
                                            flex: 1,
                                            minWidth: '200px',
                                            padding: '0.4rem',
                                            border: '1px solid #cbd5e0',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                          }}
                                          placeholder="Description"
                                        />
                                        <button
                                          onClick={() => saveEditedCode(idx)}
                                          style={{
                                            padding: '0.4rem 0.8rem',
                                            background: '#48bb78',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                          }}
                                          title="Save"
                                        >
                                          ✓
                                        </button>
                                        <button
                                          onClick={cancelEditingCode}
                                          style={{
                                            padding: '0.4rem 0.8rem',
                                            background: '#718096',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                          }}
                                          title="Cancel"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <span style={{ flex: 1 }}>
                                          <strong>{code.code}</strong> - {code.description}
                                          {code.validated && (
                                            <span style={{
                                              marginLeft: '0.5rem',
                                              color: '#38a169',
                                              fontSize: '0.9rem',
                                              fontWeight: '600'
                                            }}>
                                              ✓ Validated
                                            </span>
                                          )}
                                        </span>
                                        <button
                                          onClick={() => startEditingCode(idx, code)}
                                          style={{
                                            padding: '0.35rem 0.7rem',
                                            background: '#edf2f7',
                                            color: '#2c5282',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem',
                                            fontWeight: '500',
                                            letterSpacing: '0.01em'
                                          }}
                                          title="Edit"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          onClick={() => deleteCode(idx)}
                                          style={{
                                            padding: '0.35rem 0.7rem',
                                            background: '#fff5f5',
                                            color: '#c53030',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem',
                                            fontWeight: '500',
                                            letterSpacing: '0.01em'
                                          }}
                                          title="Delete"
                                        >
                                          Remove
                                        </button>
                                      </>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                            <button
                              onClick={addNewCode}
                              style={{
                                padding: '0.6rem 1.2rem',
                                background: '#48bb78',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              + Add Code
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Follow-up */}
                    {soapNote.clinical_support.follow_up && soapNote.clinical_support.follow_up.length > 0 && (
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#2c5282', fontSize: '1.2rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                          Follow-up Plan
                        </h4>
                        <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8', fontSize: '1.05rem', color: '#4a5568' }}>
                          {soapNote.clinical_support.follow_up.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: '0.5rem' }}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Risk Assessment */}
                    {soapNote.clinical_support.risk_assessment && soapNote.clinical_support.risk_assessment.length > 0 && (
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#2c5282', fontSize: '1.2rem', marginBottom: '0.75rem', fontWeight: '600' }}>
                          Risk Assessment
                        </h4>
                        <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8', fontSize: '1.05rem', color: '#4a5568' }}>
                          {soapNote.clinical_support.risk_assessment.map((risk, idx) => (
                            <li key={idx} style={{ marginBottom: '0.5rem' }}>{risk}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <h4>Redacted Transcript:</h4>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{soapNote.transcript}</p>
            </>
          )}
        </div>
      </div>
    </>
  );
};
